/*
 * CV2 flash offer - countdown discount on the cart page and cart drawer.
 *
 * Responsibilities:
 *   1. Start the window once per cart, storing the deadline in a cart
 *      attribute so it is server-side and survives reloads and tab switches.
 *   2. Tick the countdown and flip the card to its expired state at zero.
 *   3. Route the checkout button through /discount/<code>?redirect=/checkout
 *      so the code is applied without the shopper typing it.
 *
 * The drawer re-renders itself from the server on every cart change, so the
 * card is re-initialised on demand and the checkout handler is delegated from
 * the document rather than bound to a button that will be replaced.
 */
(function () {
  var ATTR = '_cv_flash_expires';
  var cards = [];

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  function setAttribute(value) {
    var body = { attributes: {} };
    body.attributes[ATTR] = value;
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
  }

  function expiry(card) {
    var raw = parseInt(card.getAttribute('data-expires'), 10);
    return isNaN(raw) ? null : raw;
  }

  /* The offer is live only while a deadline exists and is still in the future. */
  function liveCode() {
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var ends = expiry(card);
      if (ends && Date.now() < ends) return card.getAttribute('data-code');
    }
    return null;
  }

  function paint(card) {
    var ends = expiry(card);
    var live = card.querySelector('.cv2-flash__live');
    var dead = card.querySelector('.cv2-flash__dead');
    var clock = card.querySelector('[data-cv2-flash-clock]');
    var sr = card.querySelector('[data-cv2-flash-sr]');

    card.hidden = false;

    if (!ends) return;

    var left = ends - Date.now();

    if (left <= 0) {
      card.classList.add('is-expired');
      if (live) live.hidden = true;
      if (dead) dead.hidden = false;
      if (sr) sr.textContent = 'The discount offer has expired.';
      return;
    }

    var total = Math.ceil(left / 1000);
    var mins = Math.floor(total / 60);
    var secs = total % 60;
    if (clock) clock.textContent = pad(mins) + ':' + pad(secs);

    // Announce once a minute rather than once a second.
    if (sr && secs === 0) {
      sr.textContent = mins + (mins === 1 ? ' minute' : ' minutes') + ' left on your discount.';
    }
  }

  function startIfNeeded(card) {
    if (expiry(card)) return Promise.resolve();

    var minutes = parseInt(card.getAttribute('data-minutes'), 10) || 10;
    var ends = Date.now() + minutes * 60000;

    // Optimistic: show the countdown immediately, persist in the background.
    // If the write fails the card still counts down for this page view and the
    // deadline is re-established on the next one.
    card.setAttribute('data-expires', String(ends));
    return setAttribute(String(ends)).catch(function () {});
  }

  function collect() {
    cards = Array.prototype.slice.call(document.querySelectorAll('[data-cv2-flash]'));
    cards.forEach(function (card) {
      startIfNeeded(card).then(function () { paint(card); });
      paint(card);
    });
  }

  function tick() {
    if (!cards.length) return;
    cards.forEach(paint);
  }

  /* ------------------------------------------------------------------
     Checkout routing.

     /discount/<code>?redirect=/checkout is Shopify's own route for applying a
     code and moving on, so the code lands in the checkout session without the
     shopper typing it.

     The cart page has an explicit "Update cart" step, so a shopper can press
     Checkout with unsaved quantity edits in the inputs. Those are flushed
     first; otherwise the redirect would discard them. The drawer updates
     quantities over AJAX already and has no updates[] inputs to flush.
     ------------------------------------------------------------------ */
  function flushQuantities(form) {
    if (!form) return Promise.resolve();
    var inputs = form.querySelectorAll('input[name="updates[]"]');
    if (!inputs.length) return Promise.resolve();

    var updates = Array.prototype.map.call(inputs, function (input) {
      var n = parseInt(input.value, 10);
      return isNaN(n) || n < 0 ? 0 : n;
    });

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: updates })
    }).catch(function () {});
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('button[name="checkout"], input[name="checkout"]');
    if (!button || button.disabled) return;

    var code = liveCode();
    if (!code) return; // expired, disabled or empty cart - let the form submit normally

    event.preventDefault();
    button.setAttribute('aria-disabled', 'true');

    var form = button.form || button.closest('form');
    flushQuantities(form).then(function () {
      window.location.href =
        '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent('/checkout');
    });
  });

  /*
   * There is deliberately no way to reset the deadline from the front end. It
   * is a cart attribute, so it survives a reload, a new tab, a device switch
   * and emptying the cart - which is the point: the window has to be scarce to
   * mean anything. Resetting one for support purposes is a cart-attribute
   * write from the browser console or the Admin API.
   */
  function init() {
    collect();
    window.setInterval(tick, 1000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The drawer replaces its own markup on every cart change, and the theme
  // editor re-renders sections in place.
  document.addEventListener('shopify:section:load', collect);
  document.addEventListener('cart:refresh', collect);

  // Backstop for the drawer's own re-render, which fires no event we can hook.
  // Debounced: this page carries several apps that inject nodes continuously,
  // and re-collecting on each one would be wasteful.
  if (document.body) {
    var pending;
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        for (var j = 0; j < records[i].addedNodes.length; j++) {
          var node = records[i].addedNodes[j];
          if (node.nodeType !== 1) continue;
          if (node.matches('[data-cv2-flash]') || node.querySelector('[data-cv2-flash]')) {
            window.clearTimeout(pending);
            pending = window.setTimeout(collect, 50);
            return;
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }
})();
